import { EditorAnimationFrame } from './editor-domain.models';

export interface AnimationFramePlayerOptions {
  /** Frame timers must not trigger change detection storms; the host decides the zone. */
  runOutsideZone: (fn: () => void) => void;
  /** Called after the frame index advanced; the host re-renders the canvas. */
  onFrameAdvanced: () => void;
  /** SSR guard: with false the player never schedules timers. */
  canSchedule: boolean;
}

const MIN_FRAME_DURATION_MS = 20;
const DEFAULT_FRAME_DURATION_MS = 500;

/**
 * Client-side GIF preview player: cycles the frame index on a self-correcting
 * timeout chain (overshoot of the previous tick shortens the next delay, so
 * short frames do not drift).
 */
export class AnimationFramePlayer {
  private frames: EditorAnimationFrame[] = [];
  private framesKey = '';
  private index = 0;
  private timerId: ReturnType<typeof setTimeout> | null = null;
  private dueAt = 0;
  private isPlaying = true;

  constructor(private readonly options: AnimationFramePlayerOptions) {}

  get playing(): boolean {
    return this.isPlaying;
  }

  get frameIndex(): number {
    return this.index;
  }

  /**
   * Replaces the frame set. The schedule restarts only when the frames
   * actually changed (id/url/duration), so store echoes keep the playback
   * position.
   */
  setFrames(frames: EditorAnimationFrame[]): void {
    const framesKey = frames.map(frame => `${frame.id}:${frame.previewUrl}:${frame.durationMs}`).join('|');
    if (framesKey === this.framesKey) {
      this.frames = frames;
      return;
    }

    this.framesKey = framesKey;
    this.frames = frames;
    this.index = 0;
    this.dueAt = 0;
    this.stopTimer();
    if (frames.length > 0 && this.isPlaying) {
      this.scheduleNextFrame();
    }
  }

  setPlaying(playing: boolean): void {
    if (this.isPlaying === playing) {
      return;
    }

    this.isPlaying = playing;
    if (playing) {
      this.dueAt = 0;
      if (this.frames.length > 0) {
        this.scheduleNextFrame();
      }
    } else {
      this.stopTimer();
    }
  }

  toggle(): boolean {
    this.setPlaying(!this.isPlaying);
    return this.isPlaying;
  }

  destroy(): void {
    this.stopTimer();
  }

  private scheduleNextFrame(): void {
    if (!this.isPlaying || this.timerId !== null || !this.options.canSchedule) {
      return;
    }

    const frame = this.frames[this.index] ?? this.frames[0];
    const duration = Math.max(MIN_FRAME_DURATION_MS, frame?.durationMs ?? DEFAULT_FRAME_DURATION_MS);
    const now = performance.now();
    const overshoot = this.dueAt > 0 ? Math.min(Math.max(now - this.dueAt, 0), duration - 10) : 0;
    const delay = duration - overshoot;
    this.dueAt = now + delay;

    this.options.runOutsideZone(() => {
      this.timerId = setTimeout(() => {
        this.timerId = null;
        if (!this.isPlaying || this.frames.length === 0) {
          return;
        }

        this.index = (this.index + 1) % this.frames.length;
        this.options.onFrameAdvanced();
        this.scheduleNextFrame();
      }, delay);
    });
  }

  private stopTimer(): void {
    if (this.timerId !== null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }
}
